import { DexieStorage } from '@vp/core';
import { browserStorage, logger, openProfileDatabase } from '../platform';
import { reloadLocaleForProfile, t } from '../i18n';
import { reloadLegalForProfile } from '../legal';
import { bindProfileCard } from '../profile-card';
import { removeProfileFolder } from '../storage-location';
import { ACTIVE_KEY, adoptLegacyLocal, clearProfileLocal, setProfileScope } from './local';
import { createProfile, getActiveId, listProfiles, registryAvailable, removeProfileRecord, setActiveId, updateProfile, type ProfileRecord, type ProfileStorage } from './registry';
import { profileState } from './state';

/**
 * Starting the app in a profile (docs/TZ-PROFILES.md 4.3–4.6, 4.9). Before anything reads data:
 * the registry is opened, older data is moved into a first profile, the last active profile is
 * opened (its PIN asked) or the person creates / picks one, and the profile's database, language and
 * consent are switched to. Switching to another profile reloads the app, in every tab.
 */

/** sessionStorage: the next start shows the profile list (after "Switch profile"). */
const CHOOSE_FLAG = 'vp-choose-profile';

export type GateMode = { kind: 'create' } | { kind: 'choose' } | { kind: 'unlock'; profile: ProfileRecord };

/** What the start screen must ask before the app can open, or null. */
export async function startProfiles(): Promise<GateMode | null> {
  if (!(await registryAvailable())) {
    // nothing can be kept: a guest profile in memory (the storage banner says so)
    profileState.guest = true;
    setProfileScope('guest');
    return null;
  }
  // (nothing is written to the journal before a profile is open: the journal is the profile's)
  await migrateLegacy().catch((e: unknown) => console.warn('profile migration failed', e));
  const list = await listProfiles();
  if (!list.length) return { kind: 'create' };
  let choose = false;
  try {
    choose = sessionStorage.getItem(CHOOSE_FLAG) === '1';
    sessionStorage.removeItem(CHOOSE_FLAG);
  } catch {
    /* no session storage */
  }
  const active = await getActiveId();
  const p = list.find((x) => x.id === active);
  if (!p || choose) return { kind: 'choose' };
  if (p.pin) return { kind: 'unlock', profile: p };
  await activate(p);
  return null;
}

/** Open a profile in this page (its PIN, if any, was already checked). */
export async function activate(p: ProfileRecord): Promise<void> {
  setProfileScope(p.id);
  await setActiveId(p.id);
  const now = await updateProfile(p.id, { lastUsedAt: Date.now() }).catch(() => p);
  openProfileDatabase(now.db);
  profileState.current = now;
  bindProfileCard(now);
  reloadLocaleForProfile();
  reloadLegalForProfile();
  watchOtherTabs(now.id);
}

/** Another tab switched or deleted the profile: this one follows (one active profile for the app). */
function watchOtherTabs(id: string): void {
  window.addEventListener('storage', (e) => {
    if (e.key === ACTIVE_KEY && e.newValue !== id) window.location.reload();
  });
}

/** "Switch profile": the next start shows the list. */
export function switchProfile(): void {
  try {
    sessionStorage.setItem(CHOOSE_FLAG, '1');
  } catch {
    /* without session storage the last profile opens again */
  }
  window.location.reload();
}

/** A profile from the start screen: the active one for every tab (other tabs reload into it). */
export const enterProfile = activate;

/**
 * Delete a profile and everything of it: its folder data (asks for access: call from a click), its
 * database, its keys in localStorage and its record. Returns false when the folder could not be
 * reached (its files stay; everything else is gone).
 */
export async function deleteProfile(p: ProfileRecord): Promise<boolean> {
  const folderOk = await removeProfileFolder(p);
  if (profileState.current?.id === p.id) {
    try {
      browserStorage.close();
    } catch {
      /* not open */
    }
  }
  await deleteDatabase(p.db);
  clearProfileLocal(p.id);
  await removeProfileRecord(p.id);
  logger.info('profile.deleted', { folder: folderOk });
  return folderOk;
}

function deleteDatabase(name: string): Promise<void> {
  return new Promise((resolve) => {
    try {
      const req = indexedDB.deleteDatabase(name);
      req.onsuccess = req.onerror = () => resolve();
      // another tab still has it open: it closes on the version change and the deletion finishes
      req.onblocked = () => setTimeout(resolve, 300);
    } catch {
      resolve();
    }
  });
}

/** How many canvases a profile has (for the deletion question); null when it cannot be told. */
export async function canvasCount(p: ProfileRecord): Promise<number | null> {
  if (p.storage.kind === 'folder') return null;
  if (profileState.current?.id === p.id) return (await browserStorage.listCanvases()).length;
  const db = new DexieStorage(p.db);
  try {
    return (await db.listCanvases()).length;
  } catch {
    return null;
  } finally {
    db.close();
  }
}

// ---------------------------------------------------------------- data from before profiles

async function databaseExists(name: string): Promise<boolean> {
  if (typeof indexedDB.databases === 'function') {
    try {
      return (await indexedDB.databases()).some((d) => d.name === name);
    } catch {
      /* fall through */
    }
  }
  return false;
}

/**
 * The first start of the version with profiles on a device that has data: a profile is made from the
 * old card, keeps the old database `vp` and its folder, and gets the old keys of localStorage.
 */
export async function migrateLegacy(): Promise<ProfileRecord | null> {
  if ((await listProfiles()).length) return null;
  type OldCard = { firstName?: string; lastName?: string; avatar?: string; logo?: string };
  let card: OldCard | null = null;
  try {
    card = JSON.parse(localStorage.getItem('vp-profile-card') ?? 'null') as OldCard | null;
  } catch {
    card = null;
  }
  if (!card && !(await databaseExists('vp'))) return null;
  let storage: ProfileStorage = { kind: 'browser' };
  const old = new DexieStorage('vp');
  try {
    const loc = await old.getSetting<{ kind?: string; handle?: FileSystemDirectoryHandle; name?: string }>('storage.location');
    if (loc?.kind === 'folder' && loc.handle) storage = { kind: 'folder', mode: 'own', handle: loc.handle, name: loc.name ?? loc.handle.name };
  } catch {
    /* no old settings */
  } finally {
    old.close();
  }
  const p = await createProfile({
    firstName: card?.firstName?.trim() || t('profiles.defaultName'),
    lastName: card?.lastName ?? '',
    avatar: typeof card?.avatar === 'string' && card.avatar.startsWith('data:') ? card.avatar : '',
    logo: typeof card?.logo === 'string' && card.logo.startsWith('data:') ? card.logo : '',
    db: 'vp',
    storage,
  });
  adoptLegacyLocal(p.id);
  try {
    localStorage.removeItem('vp-profile-card');
  } catch {
    /* stays: harmless */
  }
  await setActiveId(p.id);
  return p;
}

